// Pure coordinate math for the fractal zoom — no DOM access, so it can be unit
// tested directly. Three coordinate spaces are in play:
//   - screen/CSS pixels: where the pointer actually is on the displayed element
//   - canvas pixels (0..width, 0..height): the fractal image's intrinsic grid
//   - the complex plane: the Mandelbrot coordinates a canvas pixel maps to

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Screen point -> canvas-pixel point. Scales by the ratio of the canvas's
// intrinsic size to its *displayed* size (from getBoundingClientRect), so the
// mapping stays correct however CSS has stretched the element. This scale step
// is what makes the zoom resolution-independent instead of only working when the
// display happens to equal the canvas size.
export const toCanvasPoint = (clientX, clientY, rect, canvasWidth, canvasHeight) => {
    const scaleX = canvasWidth / rect.width;
    const scaleY = canvasHeight / rect.height;
    return {
        x: clamp((clientX - rect.left) * scaleX, 0, canvasWidth),
        y: clamp((clientY - rect.top) * scaleY, 0, canvasHeight)
    };
};

// Canvas x -> real-axis value.
export const calcReal = (x, realMin, realMax, width) => {
    const eachPixel = (realMax - realMin) / width;
    return realMin + eachPixel * x;
};

// Canvas y -> imaginary-axis value. y is measured from the top of the canvas,
// but the imaginary axis increases upward, so the row is flipped.
export const calcImaginary = (y, imaginaryMin, imaginaryMax, height) => {
    const yPosition = height - y;
    const eachPixel = (imaginaryMax - imaginaryMin) / height;
    return imaginaryMin + eachPixel * yPosition;
};

// A drag rectangle in canvas pixels -> the new complex-plane bounds. The two
// corners are normalized with min/max, so a drag in any direction (including the
// bottom-right-to-top-left that's easy to do on a touch screen) yields the same,
// non-inverted bounds.
export const dragRectToBounds = ({ x0, y0, x1, y1 }, currentBounds, width, height) => {
    const { realMin, realMax, imaginaryMin, imaginaryMax } = currentBounds;

    const left = Math.min(x0, x1);
    const right = Math.max(x0, x1);
    const top = Math.min(y0, y1);
    const bottom = Math.max(y0, y1);

    return {
        realMin: calcReal(left, realMin, realMax, width),
        realMax: calcReal(right, realMin, realMax, width),
        // calcImaginary flips the row, so the larger canvas y (bottom) is the
        // smaller imaginary value.
        imaginaryMin: calcImaginary(bottom, imaginaryMin, imaginaryMax, height),
        imaginaryMax: calcImaginary(top, imaginaryMin, imaginaryMax, height)
    };
};
